import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError, get, post } from "../api/client";
import lockup from "../assets/skakuza-lockup.png";

/** Who is signed in, as `/api/v1/auth/me` describes them. */
export interface Screen {
  key: string;
  label: string;
  href: string;
  /** Whether it belongs in the main nav. A screen that is a tab inside
   * another -- Users and Spares are tabs of Providers -- is still reported,
   * so the section that owns it can draw and hide its own tab, but the shell
   * does not offer it beside the sections themselves. */
  nav: boolean;
  /** Where the shell draws it: the main nav, a tab inside its section, or
   * the account menu under the avatar (Admin, Help). */
  place: "nav" | "tab" | "account";
  /** Which run of the main nav it sits in; the shell rules a line between
   * runs. */
  group: number;
}

export interface Me {
  email: string;
  role: "office" | "provider" | "supplier";
  provider_id: number | null;
  landing: string;
  screens: Screen[];
}

export const ME_KEY = ["me"] as const;

/** The signed-in account, or null when nobody is. A 401 is an answer here,
 * not an error: it means "show the sign-in form". */
export function useMe() {
  return useQuery<Me | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await get<Me>("/api/v1/auth/me");
      } catch (error) {
        if (error instanceof ApiError && error.unauthenticated) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation<Me, ApiError, { email: string; password: string }>({
    mutationFn: (credentials) => post<Me>("/api/v1/auth/login", credentials),
    onSuccess: (me) => client.setQueryData(ME_KEY, me),
  });
}

/** The answer the server gives a temporary password: right, but it opens
 * nothing until a new password has been chosen. */
export const PASSWORD_CHANGE_REQUIRED = "password_change_required";

export function useForgotPassword() {
  return useMutation<{ detail: string }, ApiError, { email: string }>({
    mutationFn: (body) => post<{ detail: string }>("/api/v1/auth/forgot-password", body),
  });
}

export function useChangePassword() {
  const client = useQueryClient();
  return useMutation<Me, ApiError, { new_password: string; confirm: string }>({
    mutationFn: (body) => post<Me>("/api/v1/auth/change-password", body),
    onSuccess: (me) => client.setQueryData(ME_KEY, me),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation<void, ApiError>({
    mutationFn: () => post<void>("/api/v1/auth/logout"),
    onSuccess: () => {
      client.setQueryData(ME_KEY, null);
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    },
  });
}

/** Wraps everything that needs a session. Anonymous callers go to the
 * sign-in form and come back to where they were, as the pages do. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) {
    return <p className="app-loading">Loading…</p>;
  }
  if (me.isError) {
    return <p className="notice bad">Could not reach the server: {me.error.message}</p>;
  }
  if (me.data === null) {
    return <Navigate to="/login" replace state={{ next: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function LoginFrame({ children }: { children: ReactNode }) {
  return (
    <main className="login-page">
      <div className="login-frame">
        {children}
        <footer className="login-foot">
          <span>Tender 371G/2022/23</span>
          <span aria-hidden="true">·</span>
          <span>City of Cape Town</span>
        </footer>
      </div>
    </main>
  );
}

function LoginHeading({ sub }: { sub: string }) {
  return (
    <>
      <img className="login-logo" src={lockup} alt="Skakuza Trading" />
      <h1>PRV Maintenance</h1>
      <p className="login-sub">{sub}</p>
    </>
  );
}

function PasswordField({
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete: string;
  autoFocus?: boolean;
}) {
  const [shown, setShown] = useState(false);
  return (
    <label className="login-field login-password">
      <span className="visually-hidden">{placeholder}</span>
      <input
        type={shown ? "text" : "password"}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
        autoFocus={autoFocus}
      />
      <button
        type="button"
        aria-label={shown ? "Hide password" : "Show password"}
        aria-pressed={shown}
        onClick={() => setShown((was) => !was)}
      >
        {shown ? "Hide" : "Show"}
      </button>
    </label>
  );
}

export function LoginPage() {
  const me = useMe();
  const login = useLogin();
  const forgotten = useForgotPassword();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Forgetting swaps the card for an email-only form; the address typed so
  // far carries across, so nobody types it twice.
  const [forgot, setForgot] = useState(false);
  const arrived = (location.state as { notice?: string } | null)?.notice;

  if (me.data) {
    const next = (location.state as { next?: string } | null)?.next;
    return <Navigate to={next && next !== "/" ? next : me.data.landing} replace />;
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    login.mutate(
      { email: email.trim(), password },
      {
        onError: (error) => {
          if (error.status === 409 && error.message === PASSWORD_CHANGE_REQUIRED) {
            navigate("/change-password", { replace: true });
          }
        },
      },
    );
  }

  function onForgot(event: FormEvent) {
    event.preventDefault();
    forgotten.mutate({ email: email.trim() });
  }

  if (forgot) {
    return (
      <LoginFrame>
        <form className="login-card" onSubmit={onForgot}>
          <LoginHeading sub="Reset your password" />
          {forgotten.isSuccess ? (
            <p className="notice" role="status">
              {forgotten.data.detail} It works for 30 minutes; sign in with it and you will
              choose a new password.
            </p>
          ) : (
            <>
              <p className="login-hint">
                Enter your email and we will send you a temporary password.
              </p>
              <label className="login-field">
                <span className="visually-hidden">Email</span>
                <input
                  type="email"
                  autoComplete="username"
                  placeholder="Email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoFocus
                />
              </label>
              {forgotten.isError ? (
                <p className="notice bad" role="alert">
                  {forgotten.error.message}
                </p>
              ) : null}
              <button type="submit" className="login-submit" disabled={forgotten.isPending}>
                {forgotten.isPending ? "Sending…" : "Send temporary password"}
              </button>
            </>
          )}
          <button
            type="button"
            className="login-forgot"
            onClick={() => {
              forgotten.reset();
              setPassword("");
              setForgot(false);
            }}
          >
            Back to sign in
          </button>
        </form>
      </LoginFrame>
    );
  }

  const needsChange = login.isError && login.error.status === 409;
  return (
    <LoginFrame>
      <form className="login-card" onSubmit={onSubmit}>
        <LoginHeading sub="Sign in to continue" />
        {arrived && !login.isError ? (
          <p className="notice bad" role="alert">
            {arrived}
          </p>
        ) : null}
        <label className="login-field">
          <span className="visually-hidden">Email</span>
          <input
            type="email"
            autoComplete="username"
            placeholder="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
          />
        </label>
        <PasswordField
          value={password}
          onChange={setPassword}
          placeholder="Password"
          autoComplete="current-password"
        />
        {login.isError && !needsChange ? (
          <p className="notice bad" role="alert">
            {login.error.status === 401 || login.error.status === 400
              ? "That email and password do not match an account."
              : login.error.message}
          </p>
        ) : null}
        <button type="submit" className="login-submit" disabled={login.isPending}>
          {login.isPending ? "Signing in…" : "Sign in"}
        </button>
        <button
          type="button"
          className="login-forgot"
          onClick={() => {
            login.reset();
            setForgot(true);
          }}
        >
          Forgot your password?
        </button>
      </form>
    </LoginFrame>
  );
}

/** Reached only by signing in with an emailed temporary password. The
 * server holds the pending change in the session; this page chooses the new
 * password, and a successful change signs the account in. */
export function ChangePasswordPage() {
  const change = useChangePassword();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  if (change.isSuccess) {
    return <Navigate to={change.data.landing} replace />;
  }
  if (change.isError && change.error.status === 401) {
    return <Navigate to="/login" replace state={{ notice: change.error.message }} />;
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    change.mutate({ new_password: password, confirm });
  }

  const mismatch = confirm.length > 0 && password !== confirm;
  return (
    <LoginFrame>
      <form className="login-card" onSubmit={onSubmit}>
        <LoginHeading sub="Choose a new password" />
        <p className="login-hint">
          At least 12 characters. You will use it from now on; the temporary one stops working.
        </p>
        <PasswordField
          value={password}
          onChange={setPassword}
          placeholder="New password"
          autoComplete="new-password"
          autoFocus
        />
        <PasswordField
          value={confirm}
          onChange={setConfirm}
          placeholder="Confirm new password"
          autoComplete="new-password"
        />
        {mismatch ? (
          <p className="notice bad" role="alert">
            The two passwords do not match.
          </p>
        ) : change.isError ? (
          <p className="notice bad" role="alert">
            {change.error.message}
          </p>
        ) : null}
        <button
          type="submit"
          className="login-submit"
          disabled={change.isPending || mismatch || password.length < 12}
        >
          {change.isPending ? "Saving…" : "Set password and sign in"}
        </button>
        <button type="button" className="login-forgot" onClick={() => navigate("/login")}>
          Back to sign in
        </button>
      </form>
    </LoginFrame>
  );
}
